// A registry of editor languages: which file extensions map to which language,
// and which editor VIEWS (plaintext / WYSIWYG / rendered) that language supports.
// It replaces the hardcoded isMarkdownFile view-routing gate so a built-in plugin
// can later add or remove a language (and its views) by registering/disposing.
// Framework-agnostic metadata only (no CodeMirror, no DOM): mirrors the
// outline-registry pattern in ./outline.ts.

export type ViewCapability = 'plaintext' | 'wysiwyg' | 'rendered';

export interface LanguageDescriptor {
  /** Stable id (built-in name or plugin id), e.g. 'markdown', 'plaintext'. */
  id: string;
  /** Human label, e.g. 'Markdown'. */
  label: string;
  /** Lowercase extensions (no dot) this language claims, e.g. ['md','markdown']. */
  extensions: readonly string[];
  /** Canonical extension for new files of this language, e.g. 'md'. */
  primaryExt: string;
  /** Editor views this language supports; drives view routing. */
  views: readonly ViewCapability[];
}

export interface ResolvedLanguage {
  id: string;
  label: string;
  primaryExt: string;
  views: readonly ViewCapability[];
}

export interface LanguageRegistry {
  /** Register a language; returns a disposer that removes it. */
  register(d: LanguageDescriptor): () => void;
  /** Resolve a file's language by extension; the fallback when none match. */
  resolveForFile(fileName: string): ResolvedLanguage;
  /** All registered descriptors (newest first), for settings/palette listing. */
  list(): LanguageDescriptor[];
  /** Subscribe to registry changes (register / dispose). */
  subscribe(cb: () => void): () => void;
}

function extOf(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}

function toResolved(d: LanguageDescriptor): ResolvedLanguage {
  return { id: d.id, label: d.label, primaryExt: d.primaryExt, views: d.views };
}

/** Create a registry whose `fallback` resolves any file no language claims. */
export function createLanguageRegistry(fallback: LanguageDescriptor): LanguageRegistry {
  // Newest first, so a later registration overrides an earlier one.
  const langs: LanguageDescriptor[] = [];
  const listeners = new Set<() => void>();
  const emit = (): void => listeners.forEach((l) => l());

  return {
    register(d: LanguageDescriptor): () => void {
      langs.unshift(d);
      emit();
      return () => {
        const i = langs.indexOf(d);
        if (i >= 0) {
          langs.splice(i, 1);
          emit();
        }
      };
    },
    resolveForFile(fileName: string): ResolvedLanguage {
      const ext = extOf(fileName);
      const hit = ext === '' ? undefined : langs.find((d) => d.extensions.includes(ext));
      return toResolved(hit ?? fallback);
    },
    list(): LanguageDescriptor[] {
      return [...langs];
    },
    subscribe(cb: () => void): () => void {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
  };
}

/** The fallback for any file no registered language claims: plain text, one view. */
export const PLAINTEXT_LANGUAGE: LanguageDescriptor = {
  id: 'plaintext',
  label: 'Plain Text',
  extensions: [],
  primaryExt: 'txt',
  views: ['plaintext'],
};

/** Markdown: the full three-view experience. `extensions` mirror fileType.ts's
 *  MARKDOWN_EXTENSIONS byte-for-byte, so view routing stays behavior-preserving. */
export const MARKDOWN_LANGUAGE: LanguageDescriptor = {
  id: 'markdown',
  label: 'Markdown',
  extensions: ['md', 'markdown', 'mdown', 'mkd', 'mdx', 'mdwn', 'mkdn', 'markdn'],
  primaryExt: 'md',
  views: ['plaintext', 'wysiwyg', 'rendered'],
};

/** The shared registry the app resolves languages + view capabilities through. */
export const languageRegistry: LanguageRegistry = createLanguageRegistry(PLAINTEXT_LANGUAGE);
languageRegistry.register(MARKDOWN_LANGUAGE);
